import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import useGlobal from 'global-state/store';
import FileDropzone from 'components/FileDropzone';
import PdfPreviewDialog from './PdfPreviewDialog';
import {
  PDF_COMPRESSION_INPUT_MAX_BYTES,
  PDF_STORAGE_MAX_BYTES,
  compressPdf,
  compressedFileName,
  fileToDataUrl,
  formatFileSize,
  getPdfCompressionErrorCode,
} from './pdfCompression';

function errorMessage(t, code) {
  const key = `tank_creation.pdf_compression_errors.${code}`;
  const translated = t(key);
  return translated === key ? t('tank_creation.pdf_compression_errors.invalid-pdf') : translated;
}

export default function TankCertificateDropZone(props) {
  const { formik } = props;
  const { t } = useTranslation();
  const [, globalActions] = useGlobal();
  const dropzoneOpenRef = React.useRef();
  const cancellationRef = React.useRef(false);
  const isMountedRef = React.useRef(true);
  const [selectedCertificate, setSelectedCertificate] = React.useState();
  const [pendingFile, setPendingFile] = React.useState();
  const [candidate, setCandidate] = React.useState();
  const [isCompressing, setIsCompressing] = React.useState(false);
  const [compressionError, setCompressionError] = React.useState();
  const [progress, setProgress] = React.useState();
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [candidatePreviewOpen, setCandidatePreviewOpen] = React.useState(false);

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      cancellationRef.current = true;
    };
  }, []);

  const acceptFile = React.useCallback(async (file, metadata, setFileName) => {
    try {
      const data = await fileToDataUrl(file);
      if (!isMountedRef.current) {
        return;
      }
      formik.setFieldValue('originalPdfCertificate', data);
      setSelectedCertificate({ file, ...metadata });
      setFileName(metadata.name);
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      globalActions.setSnackbarMessage({
        message: errorMessage(t, getPdfCompressionErrorCode(error)),
        severity: 'error',
      });
    }
  }, [formik, globalActions, t]);

  const onDrop = React.useCallback((acceptedFiles, setFileName) => {
    const file = acceptedFiles[0];
    if (file.size <= PDF_STORAGE_MAX_BYTES) {
      acceptFile(file, {
        compressed: false,
        name: file.name,
        originalSize: file.size,
        size: file.size,
      }, setFileName);
      return;
    }
    setPendingFile({ file, setFileName });
    setCompressionError(undefined);
  }, [acceptFile]);

  const closeCompressionDialog = () => {
    if (!isCompressing) {
      setPendingFile(undefined);
      setCompressionError(undefined);
      setProgress(undefined);
    }
  };

  const startCompression = async () => {
    if (!pendingFile) {
      return;
    }
    cancellationRef.current = false;
    setCompressionError(undefined);
    setProgress(undefined);
    setIsCompressing(true);
    try {
      const result = await compressPdf(pendingFile.file, {
        isCancelled: () => cancellationRef.current,
        onProgress: (nextProgress) => {
          if (isMountedRef.current && !cancellationRef.current) {
            setProgress(nextProgress);
          }
        },
      });
      if (!isMountedRef.current || cancellationRef.current) {
        return;
      }
      setCandidate({
        file: result.blob,
        name: compressedFileName(pendingFile.file.name),
        originalSize: pendingFile.file.size,
        pageCount: result.pageCount,
        size: result.blob.size,
      });
      setCandidatePreviewOpen(true);
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      const code = getPdfCompressionErrorCode(error);
      if (code !== 'cancelled') {
        setCompressionError(code);
      }
    } finally {
      if (isMountedRef.current) {
        setIsCompressing(false);
        setProgress(undefined);
      }
    }
  };

  const cancelCompression = () => {
    cancellationRef.current = true;
  };

  const confirmCandidate = async () => {
    if (!candidate || !pendingFile) {
      return;
    }
    await acceptFile(candidate.file, {
      compressed: true,
      name: candidate.name,
      originalSize: candidate.originalSize,
      pageCount: candidate.pageCount,
      size: candidate.size,
    }, pendingFile.setFileName);
    setCandidatePreviewOpen(false);
    setCandidate(undefined);
    setPendingFile(undefined);
  };

  const closeCandidatePreview = () => {
    setCandidatePreviewOpen(false);
    setCandidate(undefined);
    setPendingFile(undefined);
  };

  const chooseAnotherFile = () => {
    cancellationRef.current = true;
    setCandidatePreviewOpen(false);
    setCandidate(undefined);
    setPendingFile(undefined);
    setCompressionError(undefined);
    setProgress(undefined);
    window.setTimeout(() => {
      if (dropzoneOpenRef.current) {
        dropzoneOpenRef.current();
      }
    }, 0);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        gap: 2,
        mt: 5,
      }}
    >
      <Typography variant="h5" component="div" sx={{ mb: 2, mt: 2 }}>
        {t('tank_creation.tank_certificate_pdf_dropzone')}
      </Typography>
      <FileDropzone
        accept={{ 'application/pdf': ['.pdf'] }}
        callback={onDrop}
        fileTooLargeMessage={t('tank_creation.pdf_compression_errors.source-too-large')}
        maxSize={PDF_COMPRESSION_INPUT_MAX_BYTES}
        openRef={dropzoneOpenRef}
        text={selectedCertificate
          ? t('tank_creation.replace_pdf')
          : t('tank_creation.upload_pdf_text')}
      />
      {selectedCertificate && (
        <Box sx={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1,
        }}
        >
          <Typography>
            {selectedCertificate.compressed
              ? t('tank_creation.pdf_compressed_size', {
                originalSize: formatFileSize(selectedCertificate.originalSize),
                compressedSize: formatFileSize(selectedCertificate.size),
              })
              : formatFileSize(selectedCertificate.size)}
          </Typography>
          <Button onClick={() => setPreviewOpen(true)}>{t('tank_creation.preview_pdf')}</Button>
          <Button onClick={() => dropzoneOpenRef.current && dropzoneOpenRef.current()}>
            {t('tank_creation.replace_pdf')}
          </Button>
        </Box>
      )}

      <Dialog
        open={Boolean(pendingFile) && !candidatePreviewOpen}
        maxWidth="sm"
        fullWidth
        onClose={closeCompressionDialog}
      >
        <DialogTitle>{t('tank_creation.pdf_compression_title')}</DialogTitle>
        <DialogContent>
          {!isCompressing && !compressionError && pendingFile && (
            <Typography>
              {t('tank_creation.pdf_compression_explanation', {
                originalSize: formatFileSize(pendingFile.file.size),
                maximumSize: formatFileSize(PDF_STORAGE_MAX_BYTES),
              })}
            </Typography>
          )}
          {isCompressing && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={24} />
                <Typography>
                  {progress
                    ? t('tank_creation.pdf_compression_progress', {
                      pageCount: progress.pageCount,
                      pageNumber: progress.pageNumber,
                      profileCount: progress.profileCount,
                      profileNumber: progress.profileIndex + 1,
                    })
                    : t('tank_creation.pdf_compression_starting')}
                </Typography>
              </Box>
              <LinearProgress />
            </Box>
          )}
          {compressionError && <Alert severity="error">{errorMessage(t, compressionError)}</Alert>}
        </DialogContent>
        <DialogActions>
          {isCompressing ? (
            <Button onClick={cancelCompression}>{t('cancel')}</Button>
          ) : (
            <>
              <Button onClick={chooseAnotherFile}>{t('tank_creation.choose_another_pdf')}</Button>
              {!compressionError && (
                <Button variant="contained" onClick={startCompression}>
                  {t('tank_creation.compress_pdf')}
                </Button>
              )}
              {compressionError && (
                <Button variant="contained" onClick={startCompression}>
                  {t('tank_creation.try_again')}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      <PdfPreviewDialog
        confirmLabel={t('tank_creation.use_compressed_pdf')}
        file={candidate && candidate.file}
        fileName={candidate && candidate.name}
        compressionMetadata={candidate && {
          finalSize: formatFileSize(candidate.size),
          originalSize: formatFileSize(candidate.originalSize),
          pageCount: candidate.pageCount,
        }}
        onClose={closeCandidatePreview}
        onConfirm={confirmCandidate}
        open={candidatePreviewOpen}
      />
      <PdfPreviewDialog
        file={selectedCertificate && selectedCertificate.file}
        fileName={selectedCertificate && selectedCertificate.name}
        onClose={() => setPreviewOpen(false)}
        open={previewOpen}
      />
    </Box>
  );
}
